import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Video, BookOpen, Users, BarChart3, MessageCircle, CheckCircle,
  ArrowRight, Play, Award, Globe, Clock, Star, FileText, Calendar
} from 'lucide-react';
import Footer from '../components/Footer';

const DigitalSchool = () => {
  const navigate = useNavigate();
  const [selectedLevel, setSelectedLevel] = useState('all');

  const stats = [
    { value: '10,000+', label: 'Students Enrolled', icon: Users },
    { value: '500+', label: 'Expert Teachers', icon: Award },
    { value: '50+', label: 'Subjects Available', icon: BookOpen },
    { value: '98%', label: 'Pass Rate', icon: BarChart3 }
  ];

  const features = [
    {
      icon: Video,
      title: 'Live Interactive Classes',
      description: 'Join live sessions with real-time interaction, screen sharing, and collaborative learning tools.'
    },
    {
      icon: BookOpen,
      title: 'Complete Curriculum',
      description: 'Aligned with WAEC, NECO, and JAMB standards covering all subjects from Primary to SS3.'
    },
    {
      icon: Users,
      title: 'Small Class Sizes',
      description: 'Maximum 15 students per class ensuring personalized attention and better engagement.'
    },
    {
      icon: BarChart3,
      title: 'Progress Analytics',
      description: 'Track performance with detailed reports, assessments, and personalized recommendations.'
    },
    {
      icon: FileText,
      title: 'Study Materials',
      description: 'Access comprehensive notes, practice questions, past papers, and video recordings.'
    },
    {
      icon: MessageCircle,
      title: '24/7 Teacher Support',
      description: 'Get help anytime through our messaging platform for homework and concept clarification.'
    }
  ];

  const levels = [
    {
      id: 'primary',
      name: 'Primary School',
      grades: 'Primary 1-6',
      students: '2,000+',
      subjects: 12,
      image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=600&h=400&fit=crop',
      description: 'Foundation learning with interactive lessons, animations, and activities for young learners.'
    },
    {
      id: 'junior',
      name: 'Junior Secondary',
      grades: 'JSS 1-3',
      students: '3,500+',
      subjects: 15,
      image: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=600&h=400&fit=crop',
      description: 'Build strong fundamentals with engaging content and preparation for senior secondary.'
    },
    {
      id: 'senior',
      name: 'Senior Secondary',
      grades: 'SS 1-3',
      students: '4,500+',
      subjects: 20,
      image: 'https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=600&h=400&fit=crop',
      description: 'Intensive WAEC, NECO, and JAMB preparation with expert strategies and practice tests.'
    }
  ];

  const subjects = [
    { name: 'Mathematics', category: 'Sciences' },
    { name: 'Physics', category: 'Sciences' },
    { name: 'Chemistry', category: 'Sciences' },
    { name: 'Biology', category: 'Sciences' },
    { name: 'English Language', category: 'Languages' },
    { name: 'Literature', category: 'Languages' },
    { name: 'Economics', category: 'Social Sciences' },
    { name: 'Government', category: 'Social Sciences' }
  ];

  const testimonials = [
    {
      name: 'Amina Mohammed',
      role: 'SS3 Student',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face',
      content: 'The digital school helped me improve from C to A in Mathematics. Teachers explain everything clearly and are always available to help.'
    },
    {
      name: 'David Okonkwo',
      role: 'Parent',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      content: 'My son has been learning here for 6 months. His confidence and grades have improved dramatically. Best decision we made.'
    },
    {
      name: 'Grace Adebayo',
      role: 'JSS2 Student',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      content: 'I love the interactive classes and how teachers make learning fun. My grades went from average to excellent in one term.'
    }
  ];

  const filteredLevels = selectedLevel === 'all' ? levels : levels.filter(l => l.id === selectedLevel);

  return (
    <div className="min-h-screen bg-white">

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-gray-50 to-white pt-20 pb-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Excellence in
                <span className="text-yellow-500"> Digital Education</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Join our digital school with live virtual classes, complete curriculum from Primary to SS3, and expert teachers guiding you every step of the way.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <button
                  onClick={() => navigate('/signup')}
                  className="inline-flex items-center justify-center px-8 py-4 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  Start Learning Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-lg border-2 border-gray-200 transition-all duration-200"
                >
                  View Schedule
                </button>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Flexible Schedule</div>
                    <div className="text-sm text-gray-600">Learn at your pace</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Globe className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Access Anywhere</div>
                    <div className="text-sm text-gray-600">Learn from home</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1588072432836-e10032774350?w=800&h=600&fit=crop"
                  alt="Online learning"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="absolute -bottom-8 -left-8 bg-white rounded-xl shadow-xl p-6 border border-gray-100">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center">
                    <Users className="w-7 h-7 text-yellow-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">10K+</div>
                    <div className="text-sm text-gray-600">Active Students</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-gray-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center">
                  <Icon className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
                  <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-gray-400">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Excel
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our digital school provides comprehensive tools and support for academic success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group p-8 rounded-2xl border border-gray-200 hover:border-yellow-500 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-yellow-500 transition-colors duration-300">
                    <Icon className="w-7 h-7 text-yellow-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* School Levels */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Choose Your Learning Level
            </h2>
            <p className="text-xl text-gray-600">
              Quality education for every stage from Primary to Senior Secondary
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => setSelectedLevel('all')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                selectedLevel === 'all'
                  ? 'bg-yellow-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
              }`}
            >
              All Levels
            </button>
            {levels.map((level) => (
              <button
                key={level.id}
                onClick={() => setSelectedLevel(level.id)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  selectedLevel === level.id
                    ? 'bg-yellow-500 text-white shadow-lg'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
                }`}
              >
                {level.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {filteredLevels.map((level, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={level.image}
                    alt={level.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 px-3 py-1 bg-white rounded-full text-sm font-semibold text-gray-900">
                    {level.grades}
                  </div>
                </div>

                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{level.name}</h3>
                  <p className="text-gray-600 mb-6">{level.description}</p>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Subjects Available</span>
                      <span className="font-semibold text-gray-900">{level.subjects}+</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Students Enrolled</span>
                      <span className="font-semibold text-gray-900">{level.students}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate('/signup')}
                    className="w-full py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-all duration-200"
                  >
                    Enroll Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600">
              Hear from students and parents in our digital school community
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                <div className="flex items-center mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed">{testimonial.content}</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Start Your Learning Journey Today
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Join thousands of students achieving excellence. First week is absolutely free.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/signup')}
              className="inline-flex items-center justify-center px-8 py-4 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Start Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <button
              onClick={() => navigate('/services-overview')}
              className="inline-flex items-center justify-center px-8 py-4 bg-white hover:bg-gray-100 text-gray-900 font-semibold rounded-lg transition-all duration-200"
            >
              Browse All Services
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DigitalSchool;
